import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Stethoscope, Users2, Building2, CalendarDays,
  Crown, BookOpen, Sparkles, MapPin, BadgeCheck, ExternalLink, Plus,
  Scale, HandHeart,
} from 'lucide-react';
import Flag from '../components/world/Flag';
import CountryProfile from '../components/world/CountryProfile';
import SuggestDialog from '../components/world/SuggestDialog';
import { useJazyk } from '../context/LanguageContext';
import { API } from '../lib/api';

/**
 * Hub jedné země – „Bloom v Česku".
 *
 * Sekce, které ještě nemají data, se nepředstírají. Buď se ukáže, co v zemi je,
 * nebo se poctivě řekne, že tam Bloom teprve roste. Vymyšlené záznamy, aby země
 * nevypadala prázdně, sem nepatří.
 */

// Části hubu. `zdroj` říká, odkud se data berou; `null` = tahle část ještě není.
// Názvy a popisy jsou ve slovníku (`sekce.*`) – stránka umí česky i anglicky.
const SEKCE = [
  { key: 'specialists', icon: Stethoscope, zdroj: 'specialists' },
  { key: 'legal', icon: Scale, zdroj: 'legal', vlastniStranka: true },
  { key: 'support', icon: HandHeart, zdroj: 'support', vlastniStranka: true },
  { key: 'organizations', icon: Building2, zdroj: 'directory' },
  { key: 'events', icon: CalendarDays, zdroj: 'directory' },
  { key: 'pageants', icon: Crown, zdroj: 'directory' },
  { key: 'resources', icon: BookOpen, zdroj: 'directory' },
  { key: 'community', icon: Users2, zdroj: null },
];

const datum = (iso, jazyk = 'cs') => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(jazyk === 'en' ? 'en-GB' : 'cs-CZ', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return '';
  }
};

export default function CountryHubPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { jazyk, t, dvojjazycne } = useJazyk();
  const [zeme, setZeme] = useState(null);
  const [obsah, setObsah] = useState({});   // { specialists: [...], organizations: [...] }
  const [domov, setDomov] = useState(null);
  const [nacita, setNacita] = useState(true);
  const [chyba, setChyba] = useState('');
  const [navrhOtevren, setNavrhOtevren] = useState(false);
  const [mesta, setMesta] = useState([]);

  useEffect(() => {
    let zivy = true;
    setNacita(true);
    setChyba('');
    (async () => {
      try {
        const { data } = await axios.get(`${API}/geo/countries/${slug}`);
        if (!zivy) return;
        setZeme(data);

        // Načteme jen ty části, které v téhle zemi opravdu něco mají –
        // zbytečné dotazy na prázdné sekce nikomu nepomůžou.
        const kNacteni = SEKCE.filter((s) => s.zdroj
          && (s.vlastniStranka || (data.counts?.[s.key] || 0) > 0));

        const [vysledky, dom] = await Promise.all([
          Promise.all(kNacteni.map((s) =>
            s.zdroj === 'specialists'
              ? axios.get(`${API}/specialists`, { params: { country_iso2: data.iso2 } })
                  .then((r) => [s.key, r.data]).catch(() => [s.key, []])
              : s.zdroj === 'legal'
              ? axios.get(`${API}/articles`, { params: { country: data.iso2 } })
                  .then((r) => [s.key, r.data]).catch(() => [s.key, []])
              : s.zdroj === 'support'
              ? axios.get(`${API}/services`, { params: { country_iso2: data.iso2 } })
                  .then((r) => [s.key, r.data]).catch(() => [s.key, []])
              : axios.get(`${API}/directory/${s.key}`, {
                  params: { country_iso2: data.iso2, limit: 12 },
                }).then((r) => [s.key, r.data.items]).catch(() => [s.key, []])
          )),
          axios.get(`${API}/geo/home-country`).catch(() => ({ data: { country: null } })),
        ]);
        if (!zivy) return;
        setObsah(Object.fromEntries(vysledky));
        setDomov(dom.data.country);

        const m = await axios.get(`${API}/directory-places/${data.iso2}`)
          .catch(() => ({ data: [] }));
        if (zivy) setMesta(m.data);
      } catch (e) {
        if (zivy) setChyba(e?.response?.status === 404
          ? 'neznama'
          : 'chyba');
      } finally {
        if (zivy) setNacita(false);
      }
    })();
    return () => { zivy = false; };
  }, [slug]);

  const nastavDomov = async () => {
    await axios.put(`${API}/geo/home-country`, { country_iso2: zeme.iso2 });
    const { data } = await axios.get(`${API}/geo/home-country`);
    setDomov(data.country);
  };

  if (nacita) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="spinner" /></div>;
  }

  if (chyba) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-bloom-sub">{t(`zeme.${chyba}`)}</p>
        <Link to="/world" className="mt-4 inline-flex items-center gap-1.5 text-bloom-violet font-medium">
          <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
        </Link>
      </div>
    );
  }

  const jeDomov = domov?.iso2 === zeme.iso2;
  const nazevZeme = dvojjazycne(zeme.name_cs, zeme.name_en);
  // Země s vyplněným profilem prázdná není, i když v ní zatím nemáme jediný
  // záznam. Hláška „nic tu nemáme" pod odstavcem o tom, jak se tam mění jméno,
  // by si protiřečila.
  const maProfil = !!zeme.intro_cs
    || Object.values(zeme.facts || {}).some((u) => u?.status && u.status !== 'unknown');
  const prazdna = zeme.total === 0 && !maProfil;
  const jenProfil = zeme.total === 0 && maProfil;
  const naplnene = SEKCE.filter((s) => !s.vlastniStranka && (obsah[s.key] || []).length > 0);

  return (
    <div data-testid="country-hub">
      <div className="relative overflow-hidden border-b border-border/40 bg-white">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-20 -left-16 w-80 h-80 rounded-full bg-bloom-pride-blue/15 blur-3xl" />
          <div className="absolute -top-10 right-0 w-80 h-80 rounded-full bg-bloom-pride-pink/20 blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-7 sm:py-10">
          <button type="button" onClick={() => navigate('/world')}
                  className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet">
            <ArrowLeft className="w-4 h-4" /> {t('zeme.mapaSveta')}
          </button>

          <div className="mt-4 flex items-start gap-4 flex-wrap">
            <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl sm:text-4xl font-extrabold text-bloom-text">
                {t('zeme.titulek', { zeme: nazevZeme })}
              </h1>
              <p className="mt-1.5 text-bloom-sub">
                {prazdna
                  ? t('zeme.prazdna')
                  : t('zeme.podtitulek', { zeme: nazevZeme })}
              </p>
            </div>

            <button type="button" onClick={() => setNavrhOtevren(true)}
                    className="inline-flex items-center gap-1.5 text-sm bg-white border border-border/50 text-bloom-sub rounded-xl px-3 py-2 hover:text-bloom-violet hover:border-bloom-violet/30"
                    data-testid="hub-suggest">
              <Plus className="w-4 h-4" /> {t('zeme.navrhnout')}
            </button>

            {jeDomov ? (
              <span className="inline-flex items-center gap-1.5 text-sm bg-bloom-mint/15 border border-bloom-mint/40 rounded-xl px-3 py-2 text-bloom-text">
                <Home className="w-4 h-4 text-bloom-violet" /> {t('zeme.domovska')}
              </span>
            ) : (
              <button type="button" onClick={nastavDomov}
                      className="inline-flex items-center gap-1.5 text-sm bg-white border border-bloom-violet/30 text-bloom-violet rounded-xl px-3 py-2 hover:bg-bloom-violet/5"
                      data-testid="set-home-country">
                <Home className="w-4 h-4" /> {t('zeme.nastavitDomovskou')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-7 sm:py-9">
        {/* Co v zemi platí, patří nad seznamy: kvůli tomu sem člověk chodí. */}
        <CountryProfile zeme={zeme} />

        {/* Kde v téhle zemi vůbec něco je. Rychlejší než projít všechny sekce
            a hledat, jestli je něco poblíž. Ukazuje se až od dvou měst –
            u jednoho by to byl filtr, který nic nefiltruje. */}
        {mesta.length > 1 && (
          <section className="mt-6" data-testid="hub-mesta">
            <h2 className="font-heading text-lg font-bold text-bloom-text">{t('zeme.kdeToJe')}</h2>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {mesta.slice(0, 20).map((m) => (
                <Link key={m.city_name}
                      to={`/world/${slug}/organizations?mesto=${encodeURIComponent(m.city_name)}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-border/50 text-bloom-sub hover:border-bloom-violet/40 hover:text-bloom-violet">
                  <MapPin className="w-3 h-3" />{m.city_name}
                  <span className="opacity-70">{m.total}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {prazdna && (
          <div className="bg-white border border-border/40 rounded-3xl p-6 sm:p-8 text-center">
            <Sparkles className="w-8 h-8 text-bloom-violet mx-auto" />
            <h2 className="mt-3 font-heading text-xl font-bold text-bloom-text">
              {t('zeme.rosteTitulek')}
            </h2>
            <p className="mt-2 text-bloom-sub max-w-lg mx-auto">
              {t('zeme.rostePopis')}
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <button type="button" onClick={() => setNavrhOtevren(true)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-5 py-2.5 rounded-xl"
                      data-testid="empty-suggest">
                <Plus className="w-4 h-4" /> {t('zeme.navrhnout')}
              </button>
              <Link to="/world"
                    className="inline-flex items-center gap-2 text-sm font-medium text-bloom-violet bg-white border border-bloom-violet/25 px-5 py-2.5 rounded-xl hover:bg-bloom-violet/5">
                {t('zeme.jinouZemi')}
              </Link>
            </div>
          </div>
        )}

        {jenProfil && (
          <div className="mt-6 rounded-2xl border border-border/40 bg-white p-5 text-center">
            <p className="text-bloom-text">
              {t('zeme.bezKontaktu')}
            </p>
            <p className="mt-1 text-sm text-bloom-sub">
              {t('zeme.bezKontaktuPopis')}
            </p>
            <button type="button" onClick={() => setNavrhOtevren(true)}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-5 py-2.5 rounded-xl">
              <Plus className="w-4 h-4" /> {t('zeme.navrhnout')}
            </button>
          </div>
        )}

        {naplnene.map((sekce) => (
          <Sekce key={sekce.key} sekce={sekce} polozky={obsah[sekce.key]} slug={slug}
                 celkem={zeme.counts?.[sekce.key] || obsah[sekce.key]?.length || 0} />
        ))}

        <section className={naplnene.length > 0 ? 'mt-10' : 'mt-8'}>
          <h2 className="font-heading text-lg font-bold text-bloom-text">{t('zeme.coTuBude')}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SEKCE.map(({ key, icon: Icon, zdroj }) => {
              // Počet z načteného obsahu, když ho máme – `counts` ze serveru
              // právo ani podporu nezná a psalo by u nich pořád „zatím prázdné".
              const pocet = obsah[key]?.length ?? zeme.counts?.[key] ?? 0;
              const hotovo = !!zdroj;
              // Hotová sekce je odkaz i když je prázdná: jinak se do ní nedá
              // dostat a nikdo do ní nemůže přidat to první.
              const Obal = hotovo ? Link : 'div';
              const obalProps = hotovo
                ? { to: `/world/${slug}/${key}`, 'data-testid': `hub-sekce-${key}` }
                : {};
              return (
                <Obal key={key} {...obalProps}
                     className={`block rounded-2xl p-4 border transition-colors ${hotovo
                       ? 'bg-white border-border/40 hover:border-bloom-violet/40'
                       : 'bg-bloom-bg border-dashed border-border/60'}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${hotovo ? 'text-bloom-violet' : 'text-bloom-sub/50'}`} />
                    <h3 className={`font-heading font-bold ${hotovo ? 'text-bloom-text' : 'text-bloom-sub'}`}>
                      {t(`sekce.${key}`)}
                    </h3>
                  </div>
                  <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed">
                    {t(`sekce.${key}.popis`)}
                  </p>
                  <p className="mt-2 text-xs">
                    {hotovo
                      ? <span className="text-bloom-violet font-medium">
                          {pocet > 0
                            ? t('vypis.zaznamu', { pocet })
                            : t('zeme.zatimPrazdne')}
                        </span>
                      : <span className="text-bloom-sub/60">{t('zeme.pripravujeme')}</span>}
                  </p>
                </Obal>
              );
            })}
          </div>
        </section>
      </div>

      <SuggestDialog
        open={navrhOtevren}
        onOpenChange={setNavrhOtevren}
        countryIso2={zeme.iso2}
        countryName={nazevZeme}
      />
    </div>
  );
}

function Sekce({ sekce, polozky, slug, celkem }) {
  const { t } = useJazyk();
  const Icon = sekce.icon;
  return (
    <section className="mb-10" data-testid={`hub-section-${sekce.key}`}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-bold text-bloom-text flex items-center gap-2">
          <Icon className="w-5 h-5 text-bloom-violet" />
          {t(`sekce.${sekce.key}`)}
        </h2>
        {/* Odborníci mají vlastní stránku, ne katalogový výpis, ale adresa je
            stejná – rozlišuje se až v routách. */}
        {sekce.zdroj ? (
          <Link to={`/world/${slug}/${sekce.key}`}
                className="text-sm text-bloom-violet hover:underline shrink-0"
                data-testid={`hub-more-${sekce.key}`}>
            {t('zeme.zobrazitVse', { pocet: celkem })}
          </Link>
        ) : (
          <span className="text-sm text-bloom-sub">
            {t('vypis.zaznamu', { pocet: polozky.length })}
          </span>
        )}
      </div>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {polozky.map((p) => (
          <li key={p.id}>
            {sekce.key === 'specialists'
              ? <KartaOdbornika zaznam={p} slug={slug} />
              : <KartaZaznamu zaznam={p} kind={sekce.key} slug={slug} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

function KartaOdbornika({ zaznam, slug }) {
  // Dřív to mířilo na `/specialists?id=…`, jenže stará stránka odborníků
  // `id` v adrese vůbec nečte a umí filtrovat jen Česko/svět. Odkaz tedy
  // skončil v seznamu českých odborníků. Teď vede rovnou na detail.
  return (
    <Link to={`/world/${slug}/specialists/${zaznam.id}`}
          className="block h-full bg-white border border-border/40 rounded-2xl p-4 hover:border-bloom-violet/40 hover:shadow-sm transition-all">
      <p className="font-heading font-bold text-bloom-text">{zaznam.name}</p>
      <p className="text-sm text-bloom-violet mt-0.5">{zaznam.specialty}</p>
      {(zaznam.location?.city_name || zaznam.city) && (
        <p className="text-xs text-bloom-sub mt-1.5 flex items-center gap-1">
          <MapPin className="w-3 h-3" />{zaznam.location?.city_name || zaznam.city}
        </p>
      )}
    </Link>
  );
}

function KartaZaznamu({ zaznam, kind, slug }) {
  const { t, datum } = useJazyk();
  const d = zaznam.details || {};

  return (
    <Link to={`/world/${slug}/${kind}/${zaznam.slug || zaznam.id}`}
          className="block h-full bg-white border border-border/40 rounded-2xl p-4 hover:border-bloom-violet/40 hover:shadow-sm transition-all">
      <div className="flex items-start gap-2">
        <p className="font-heading font-bold text-bloom-text flex-1">{zaznam.name}</p>
        {zaznam.verification === 'verified' && (
          <span title={t('kat.overenoZaznam')}>
            <BadgeCheck className="w-4 h-4 text-bloom-mint shrink-0" />
          </span>
        )}
      </div>

      {zaznam.description && (
        <p className="mt-1.5 text-sm text-bloom-sub leading-relaxed line-clamp-3">
          {zaznam.description}
        </p>
      )}

      <div className="mt-2.5 space-y-1">
        {kind === 'events' && d.starts_at && (
          <p className="text-xs text-bloom-violet font-medium">{datum(d.starts_at)}</p>
        )}
        {kind === 'pageants' && d.registration_deadline && (
          <p className="text-xs text-bloom-violet font-medium">
            {t('kat.prihlaskyDo', { datum: datum(d.registration_deadline) })}
          </p>
        )}
        {zaznam.location?.city_name && (
          <p className="text-xs text-bloom-sub flex items-center gap-1">
            <MapPin className="w-3 h-3" />{zaznam.location.city_name}
          </p>
        )}
      </div>

      <span className="mt-3 inline-flex items-center gap-1 text-xs text-bloom-violet">
        Zobrazit detail <ExternalLink className="w-3 h-3" />
      </span>
    </Link>
  );
}
